import Bars3Icon from '@heroicons/react/24/outline/Bars3Icon';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import usePayment from '../modules/patients/hooks/UsePayment';
import { useAuth } from '../shared/context/useAuth';
import { supabase } from '../data/supabase';
import moment from 'moment';
import { creditService } from '../services/creditService';

function Header() {
  const { admin, patient } = useAuth();
  const history = useHistory();
  const { voucher } = usePayment();
  const [packages, setPackages] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar dados do usuário
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (!patient || !patient[0]?.id) return;
        
        // Verificar pacotes expirados antes de carregar dados
        await creditService.checkExpiredPackages(patient[0].id);
        
        // Buscar todos os dados
        const { packages, wallets, transactions } = await fetchAllCreditData(patient[0].id);
        
        setPackages(packages || []);
        setWallets(wallets || []);
        setTransactions(transactions || []);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setError('Não foi possível carregar seus créditos. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Configurar atualização periódica a cada 5 minutos
    const intervalId = setInterval(() => {
      fetchData();
    }, 300000); // 5 minutos
    
    return () => clearInterval(intervalId);
  }, [patient]);

  // Função para buscar todos os dados de crédito
  const fetchAllCreditData = async (patientId) => {
    try {
      // Buscar todos os pacotes do paciente
      const { data: packagesData, error: packagesError } = await supabase
        .from('packages')
        .select(`
          *,
          sessions:sessions(*)
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });
      
      if (packagesError) throw packagesError;
      
      // Buscar carteiras do paciente
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId);
      
      if (walletsError) throw walletsError;
      
      // Buscar transações de crédito para todas as carteiras
      let transactionsData = [];
      if (walletsData && walletsData.length > 0) {
        const { data: txData, error: transactionsError } = await supabase
          .from('credit_transactions')
          .select(`
            *,
            wallet:wallet_id(*),
            appointment:appointment_id(*)
          `)
          .in('wallet_id', walletsData.map(w => w.id))
          .order('created_at', { ascending: false });
        
        if (transactionsError) throw transactionsError;
        transactionsData = txData || [];
      }
      
      return {
        packages: packagesData || [],
        wallets: walletsData || [],
        transactions: transactionsData || []
      };
    } catch (error) {
      console.error('Erro ao buscar dados de crédito:', error);
      throw error;
    }
  };

  // Função para formatar o tipo de crédito/pacote
  const formatCreditType = (type) => {
    if (!type) return 'Individual';
    
    switch(type) {
      case 'individual':
        return 'Individual';
      case 'casal':
        return 'Casal';
      case 'familia':
        return 'Família';
      case 'package':
        return 'Pacote';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Função para obter a classe de cor baseada no tipo
  const getTypeColorClass = (type) => {
    if (!type) return 'border-[#3BBED0]';
    
    switch(type) {
      case 'individual':
        return 'border-blue-500';
      case 'casal':
        return 'border-purple-500';
      case 'familia':
        return 'border-green-500';
      case 'package':
        return 'border-orange-500';
      default:
        return 'border-[#3BBED0]';
    }
  };

  // Função para obter o ícone baseado no tipo
  const getTypeIcon = (type) => {
    if (!type) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#3BBED0]" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      );
    }
    
    switch(type) {
      case 'individual':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        );
      case 'casal':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
          </svg>
        );
      case 'familia':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
        );
      case 'package':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Função para calcular o saldo de créditos de pacote por tipo de sessão
  const calculatePackageCredits = (sessionType) => {
    // Filtrar transações de pacote pelo tipo de sessão
    const packageTransactions = transactions.filter(t => 
      t.source_type === 'package' && 
      (t.wallet?.type === sessionType || 
       (t.notes && t.notes.toLowerCase().includes(sessionType.toLowerCase())))
    );
    
    const totalCredits = packageTransactions.reduce((sum, t) => 
      sum + (t.amount > 0 ? t.amount : 0), 0);
      
    const usedCredits = packageTransactions.reduce((sum, t) => 
      sum + (t.amount < 0 ? -t.amount : 0), 0);
      
    return {
      total: totalCredits,
      used: usedCredits,
      available: totalCredits - usedCredits
    };
  };
  
  // Função para calcular o saldo de créditos individuais por tipo de sessão
  const calculateIndividualCredits = (sessionType) => {
    // Filtrar transações de crédito individual pelo tipo de sessão
    const individualTransactions = transactions.filter(t => 
      t.source_type === 'individual_credit' && 
      (t.wallet?.type === sessionType || 
       (t.notes && t.notes.toLowerCase().includes(sessionType.toLowerCase())))
    );
    
    const totalCredits = individualTransactions.reduce((sum, t) => 
      sum + (t.amount > 0 ? t.amount : 0), 0);
      
    const usedCredits = individualTransactions.reduce((sum, t) => 
      sum + (t.amount < 0 ? -t.amount : 0), 0);
      
    return {
      total: totalCredits,
      used: usedCredits,
      available: totalCredits - usedCredits
    };
  };

  // Função para obter os tipos de sessão únicos das transações
  const getUniqueSessionTypes = (sourceType) => {
    // Filtrar transações pelo tipo de fonte (package ou individual_credit)
    const filteredTransactions = transactions.filter(t => t.source_type === sourceType);
    
    // Extrair tipos de sessão das carteiras e notas
    const types = new Set();
    
    filteredTransactions.forEach(t => {
      if (t.wallet?.type && t.wallet.type !== 'package' && t.wallet.type !== 'individual') {
        types.add(t.wallet.type);
      }
      
      // Tentar extrair tipo das notas
      if (t.notes) {
        const lowerNotes = t.notes.toLowerCase();
        if (lowerNotes.includes('individual')) types.add('individual');
        if (lowerNotes.includes('casal')) types.add('casal');
        if (lowerNotes.includes('família') || lowerNotes.includes('familia')) types.add('familia');
      }
    });
    
    // Se nenhum tipo específico for encontrado, adicionar 'individual' como padrão
    if (types.size === 0) {
      types.add('individual');
    }
    
    return Array.from(types);
  };

  function logoutUser() {
    localStorage.clear();
    window.location.href = `https://www.embraceinternacional.com/`;
  }

  const avatarUrl = useSelector((state) => state.avatar.avatar);

  // Obter tipos de sessão únicos para pacotes e créditos individuais
  const packageSessionTypes = getUniqueSessionTypes('package');
  const individualSessionTypes = getUniqueSessionTypes('individual_credit');

  // Verificar se há transações de pacote e individuais
  const hasPackageTransactions = transactions.some(t => t.source_type === 'package');
  const hasIndividualTransactions = transactions.some(t => t.source_type === 'individual_credit');

  // Calcular total de créditos disponíveis
  let totalAvailableCredits = 0;
  
  // Somar créditos de pacotes
  packageSessionTypes.forEach(sessionType => {
    const credits = calculatePackageCredits(sessionType);
    totalAvailableCredits += credits.available;
  });
  
  // Somar créditos individuais
  individualSessionTypes.forEach(sessionType => {
    const credits = calculateIndividualCredits(sessionType);
    totalAvailableCredits += credits.available;
  });

  return (
    <div className="navbar flex justify-between dark:bg-base-100 bg-white z-10 shadow-sm">
      <div className="">
        <label
          htmlFor="left-sidebar-drawer"
          className="btn btn-primary drawer-button laptop:hidden desktop:hidden"
        >
          <Bars3Icon className="h-5 inline-block w-5" />
        </label>
      </div>

      <div className="order-last">
        {/* Carteira (Créditos e Pacotes) */}
        {Object.keys(patient)?.length > 0 && (
          <div className="dropdown dropdown-bottom dropdown-end">
            <div
              tabIndex={0}
              role="button"
              className="bg-[#3bbdcf] text-sm font-bold text-white rounded-xl p-2 m-1 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span className="hidden sm:inline">Carteira</span>
              {totalAvailableCredits > 0 && (
                <div className="badge badge-sm badge-accent text-white ml-1">
                  {totalAvailableCredits}
                </div>
              )}
            </div>
            <div
              tabIndex={0}
              className="dropdown-content z-[1] card shadow-lg bg-white text-primary-content max-w-[90vw] w-80 sm:w-96"
            >
              <div className="card-body p-4 text-[black]">
                {/* Resumo Rápido */}
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-lg text-[#333]">Sua Carteira</h3>
                  <div className="text-xs text-gray-500">
                    {loading ? (
                      <span className="inline-block w-4 h-4 border-2 border-[#3bbdcf] border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      `Total: ${totalAvailableCredits} sessões disponíveis`
                    )}
                  </div>
                </div>
                
                {/* Seção de Créditos Individuais */}
                <div className="mb-3">
                  <h3 className="font-medium text-[#333] text-base mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Sessões Individuais
                  </h3>
                  
                  {hasIndividualTransactions ? (
                    <div className="flex flex-wrap gap-2">
                      {individualSessionTypes.map((sessionType, index) => {
                        const credits = calculateIndividualCredits(sessionType);
                        
                        // Não exibir se não houver créditos disponíveis
                        if (credits.available <= 0) return null;
                        
                        return (
                          <div key={index} className="badge badge-success gap-1 py-3">
                            <span className="font-bold">{credits.available}</span>
                            <span className="text-xs">Sessão {formatCreditType(sessionType)}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      Você não possui créditos individuais
                    </div>
                  )}
                </div>
                
                {/* Seção de Pacotes */}
                <div className="mt-2 pt-3 border-t border-gray-200">
                  <h3 className="font-medium text-[#333] text-base mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Pacotes de Sessões
                  </h3>
                  
                  {hasPackageTransactions ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                      {packageSessionTypes.map((sessionType, index) => {
                        const credits = calculatePackageCredits(sessionType);
                        
                        // Não exibir se não houver créditos disponíveis
                        if (credits.available <= 0) return null;
                        
                        // Encontrar o pacote correspondente para obter a data de expiração
                        const matchingPackage = packages.find(pkg => pkg.type === sessionType);
                        
                        return (
                          <div key={index} className="bg-gray-50 rounded-md p-2">
                            <div className="text-sm font-medium flex justify-between">
                              <span className="text-[#3bbdcf] capitalize">Pacote {formatCreditType(sessionType)}</span>
                              <span className="font-bold">{credits.available} sessões</span>
                            </div>
                            {matchingPackage && (
                              <div className="text-xs text-gray-500 mt-1">
                                Expira em {moment(matchingPackage.package_expiry).format('DD/MM/YYYY')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      Você não possui pacotes ativos
                    </div>
                  )}
                </div>
                
                {/* Botões de Ação */}
                <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => history.push('/consultas')}
                    className="btn btn-sm btn-success flex-1 h-auto py-2"
                    disabled={totalAvailableCredits === 0}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Agendar Sessão
                  </button>
                  
                  <button
                    onClick={() => history.push('/pagamentos')}
                    className="btn btn-sm bg-[#3bbdcf] text-white hover:bg-[#2aa9bd] flex-1 h-auto py-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Comprar
                  </button>
                </div>
                
                {/* Link para histórico completo */}
                <div className="text-center mt-2">
                  <Link 
                    to="/historico" 
                    className="text-xs text-[#3bbdcf] hover:underline"
                  >
                    Ver histórico completo →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile icon, opening menu on click */}
        <div className="dropdown dropdown-end ml-2">
          <label tabIndex={0} className="btn btn-ghost btn-circle avatar">
            <div className="w-10 rounded-full">
              <img
                src={
                  avatarUrl || 'https://cdn-icons-png.flaticon.com/512/6596/6596121.png' // Caminho de fallback
                }
                alt="avatar"
              />
            </div>
          </label>
          <ul
            tabIndex={0}
            className="menu menu-compact dropdown-content mt-3 p-2 shadow bg-base-100 rounded-box w-52"
          >
            {Object.keys(admin)?.length === 0 && (
              <>
                <li className="justify-between">
                  <Link to="/perfil">Editar Perfil</Link>
                </li>
                <div className="divider mt-0 mb-0" />
              </>
            )}

            <li>
              <span onClick={logoutUser}>Sair</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Header;